import { Editor } from '@tiptap/react'
import { StyleType } from '../../../../../utils/markdown'
import { UploadedFile } from '../../../../../utils/file'

const INLINE_SYNTAX: Partial<Record<StyleType, string>> = {
  [StyleType.BOLD]: '**',
  [StyleType.ITALIC]: '*',
  [StyleType.STRIKETHROUGH]: '~~',
  [StyleType.CODE]: '`',
}

const HEADING_PREFIXES: Partial<Record<StyleType, string>> = {
  [StyleType.HEADER_ONE]: '# ',
  [StyleType.HEADER_TWO]: '## ',
  [StyleType.HEADER_THREE]: '### ',
  [StyleType.HEADER_FOUR]: '#### ',
  [StyleType.HEADER_FIVE]: '##### ',
  [StyleType.HEADER_SIX]: '###### ',
}

const LIST_PREFIXES: Partial<Record<StyleType, string>> = {
  [StyleType.UNORDERED_LIST_ITEM]: '- ',
  [StyleType.ORDERED_LIST_ITEM]: '1. ',
  [StyleType.TASK_LIST]: '- [ ] ',
}

const BLOCK_PREFIXES: Partial<Record<StyleType, string>> = {
  ...HEADING_PREFIXES,
  [StyleType.BLOCKQUOTE]: '> ',
  ...LIST_PREFIXES,
}

const ALL_HEADING_PREFIX_VALUES = Object.values(HEADING_PREFIXES)
const ALL_LIST_PREFIX_VALUES = Object.values(LIST_PREFIXES)

export const applyMarkdownSyntax = (editor: Editor, styleType: StyleType) => {
  if (INLINE_SYNTAX[styleType]) {
    applyInlineSyntax(editor, styleType)
    return
  }

  const blockPrefix = BLOCK_PREFIXES[styleType]
  if (blockPrefix) {
    applyBlockPrefix(editor, blockPrefix, getConflictingPrefixes(styleType))
    return
  }

  if (styleType === StyleType.CODE_BLOCK) {
    applyCodeBlock(editor)
    return
  }

  if (styleType === StyleType.HORIZONTAL_RULE) {
    applyHorizontalRule(editor)
  }
}

const getConflictingPrefixes = (styleType: StyleType): string[] => {
  if (HEADING_PREFIXES[styleType]) return ALL_HEADING_PREFIX_VALUES
  if (LIST_PREFIXES[styleType]) return ALL_LIST_PREFIX_VALUES
  return []
}

// マーカー文字（*, ~, `）かどうか
const MARKER_CHARS = new Set(['*', '~', '`'])

// 選択テキストの両端からインラインマーカーを剥がし、コンテンツと適用中のスタイル集合を返す
const parseInlineStyles = (
  text: string
): { content: string; styles: Set<StyleType> } => {
  const styles = new Set<StyleType>()
  let content = text
  let changed = true

  while (changed) {
    changed = false
    if (
      content.length > 4 &&
      content.startsWith('**') &&
      content.endsWith('**')
    ) {
      styles.add(StyleType.BOLD)
      content = content.slice(2, -2)
      changed = true
    }
    if (
      content.length > 4 &&
      content.startsWith('~~') &&
      content.endsWith('~~')
    ) {
      styles.add(StyleType.STRIKETHROUGH)
      content = content.slice(2, -2)
      changed = true
    }
    if (
      content.length > 2 &&
      content.startsWith('*') &&
      content.endsWith('*') &&
      content[1] !== '*'
    ) {
      styles.add(StyleType.ITALIC)
      content = content.slice(1, -1)
      changed = true
    }
    if (
      content.length > 2 &&
      content.startsWith('`') &&
      content.endsWith('`') &&
      !content.startsWith('```')
    ) {
      styles.add(StyleType.CODE)
      content = content.slice(1, -1)
      changed = true
    }
  }

  return { content, styles }
}

// スタイル集合からマークダウンテキストを再構築する（内側から順に巻く）
const STYLE_WRAP_ORDER: { style: StyleType; marker: string }[] = [
  { style: StyleType.CODE, marker: '`' },
  { style: StyleType.STRIKETHROUGH, marker: '~~' },
  { style: StyleType.ITALIC, marker: '*' },
  { style: StyleType.BOLD, marker: '**' },
]

const wrapWithStyles = (content: string, styles: Set<StyleType>): string => {
  let result = content
  for (const { style, marker } of STYLE_WRAP_ORDER) {
    if (styles.has(style)) {
      result = `${marker}${result}${marker}`
    }
  }
  return result
}

const applyInlineSyntax = (editor: Editor, styleType: StyleType) => {
  const { from, to } = editor.state.selection
  if (from === to) return

  // 選択範囲の前後にマーカー文字があれば、それも含めて処理対象にする
  // （ユーザーがコンテンツだけを選択した場合にも正しくトグルできるようにする）
  const { $from, $to } = editor.state.selection
  const paraStart = $from.start()
  const paraEnd = $to.end()

  let processFrom = from
  while (processFrom > paraStart) {
    const char = editor.state.doc.textBetween(processFrom - 1, processFrom)
    if (!MARKER_CHARS.has(char)) break
    processFrom--
  }

  let processTo = to
  while (
    processTo < paraEnd &&
    MARKER_CHARS.has(editor.state.doc.textBetween(processTo, processTo + 1))
  ) {
    processTo++
  }

  const textToProcess = editor.state.doc.textBetween(processFrom, processTo)
  const { content, styles } = parseInlineStyles(textToProcess)

  // パースでマーカーが見つからなかった場合は元の選択範囲で処理
  if (content === textToProcess) {
    processFrom = from
    processTo = to
  }

  // スタイルをトグル
  if (styles.has(styleType)) {
    styles.delete(styleType)
  } else {
    styles.add(styleType)
  }

  const result = styles.size > 0 ? wrapWithStyles(content, styles) : content

  editor
    .chain()
    .focus()
    .command(({ tr }) => {
      tr.insertText(result, processFrom, processTo)
      return true
    })
    .run()
}

const applyBlockPrefix = (
  editor: Editor,
  prefix: string,
  conflictingPrefixes: string[]
) => {
  editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      const { from, to } = state.selection

      // 選択範囲内の全段落を収集
      const paragraphs: { start: number; end: number; text: string }[] = []
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === 'paragraph') {
          const contentStart = pos + 1
          const contentEnd = contentStart + node.content.size
          paragraphs.push({
            start: contentStart,
            end: contentEnd,
            text: node.textContent,
          })
        }
      })

      if (paragraphs.length === 0) return true

      // 全行が既に同じprefixを持つならトグルオフ
      const allHavePrefix = paragraphs.every(p => p.text.startsWith(prefix))

      // 位置がずれないよう逆順で処理
      for (let i = paragraphs.length - 1; i >= 0; i--) {
        const p = paragraphs[i]

        if (allHavePrefix) {
          tr.insertText(p.text.slice(prefix.length), p.start, p.end)
        } else {
          const existing = conflictingPrefixes.find(cp => p.text.startsWith(cp))
          if (existing) {
            tr.insertText(
              prefix + p.text.slice(existing.length),
              p.start,
              p.end
            )
          } else if (!p.text.startsWith(prefix)) {
            tr.insertText(prefix + p.text, p.start, p.end)
          }
        }
      }

      return true
    })
    .run()
}

const applyCodeBlock = (editor: Editor) => {
  editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      const { from, to } = state.selection
      const schema = state.schema

      // 選択範囲の段落を収集
      const paragraphs: { node: typeof state.doc; pos: number }[] = []
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type.name === 'paragraph') {
          paragraphs.push({ node, pos })
        }
      })

      if (paragraphs.length === 0) return true

      const firstText = paragraphs[0].node.textContent
      const lastText = paragraphs[paragraphs.length - 1].node.textContent
      const lastPara = paragraphs[paragraphs.length - 1]
      const startPos = paragraphs[0].pos
      const endPos = lastPara.pos + lastPara.node.nodeSize

      if (
        firstText.startsWith('```') &&
        lastText === '```' &&
        paragraphs.length >= 2
      ) {
        // トグルオフ: フェンス行を除去してコンテンツだけ残す
        const contentNodes = paragraphs
          .slice(1, -1)
          .map(p => p.node.copy(p.node.content))
        tr.replaceWith(
          startPos,
          endPos,
          contentNodes.length > 0 ? contentNodes : schema.node('paragraph')
        )
      } else {
        // トグルオン: フェンス行で囲む
        const fenceOpen = schema.node('paragraph', null, [schema.text('```')])
        const fenceClose = schema.node('paragraph', null, [schema.text('```')])
        const contentNodes = paragraphs.map(p => p.node.copy(p.node.content))
        tr.replaceWith(startPos, endPos, [
          fenceOpen,
          ...contentNodes,
          fenceClose,
        ])
      }

      return true
    })
    .run()
}

const applyHorizontalRule = (editor: Editor) => {
  editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      const { $from } = state.selection
      const paraStart = $from.start()
      const paraEnd = $from.end()
      const lineText = state.doc.textBetween(paraStart, paraEnd)
      if (lineText.trim() === '***' || lineText.trim() === '---') {
        tr.insertText('', paraStart, paraEnd)
      } else {
        tr.insertText('***', paraStart, paraEnd)
      }
      return true
    })
    .run()
}

// --- ハイライト検出 ---

const countOccurrences = (text: string, marker: string): number => {
  let count = 0
  let pos = 0
  while ((pos = text.indexOf(marker, pos)) !== -1) {
    count++
    pos += marker.length
  }
  return count
}

export const findHighlightItemsInMarkdownSource = (
  editor: Editor
): string[] => {
  const { from, to } = editor.state.selection
  const { $from } = editor.state.selection
  const paraStart = $from.start()
  const paraEnd = $from.end()
  const lineText = editor.state.doc.textBetween(paraStart, paraEnd)
  const cursorOffset = $from.pos - paraStart

  const items: string[] = []

  // ブロックレベル（行プレフィックス）
  if (lineText.startsWith('###### ')) items.push(StyleType.HEADER_SIX)
  else if (lineText.startsWith('##### ')) items.push(StyleType.HEADER_FIVE)
  else if (lineText.startsWith('#### ')) items.push(StyleType.HEADER_FOUR)
  else if (lineText.startsWith('### ')) items.push(StyleType.HEADER_THREE)
  else if (lineText.startsWith('## ')) items.push(StyleType.HEADER_TWO)
  else if (lineText.startsWith('# ')) items.push(StyleType.HEADER_ONE)

  if (lineText.startsWith('> ')) items.push(StyleType.BLOCKQUOTE)
  if (lineText.startsWith('- [ ] ') || lineText.startsWith('- [x] ')) {
    items.push(StyleType.TASK_LIST)
  } else if (lineText.startsWith('- ')) {
    items.push(StyleType.UNORDERED_LIST_ITEM)
  }
  if (/^\d+\. /.test(lineText)) items.push(StyleType.ORDERED_LIST_ITEM)
  if (lineText.trim() === '***' || lineText.trim() === '---') {
    items.push(StyleType.HORIZONTAL_RULE)
  }

  // インラインレベル（カーソル前のマーカー出現回数の奇偶で判定）
  // ** は * の偶数個分なので、* の奇偶判定に影響しない
  const beforeCursor = lineText.substring(0, cursorOffset)
  if (countOccurrences(beforeCursor, '**') % 2 === 1) {
    items.push(StyleType.BOLD)
  }
  if (countOccurrences(beforeCursor, '*') % 2 === 1) {
    items.push(StyleType.ITALIC)
  }
  if (countOccurrences(beforeCursor, '~~') % 2 === 1) {
    items.push(StyleType.STRIKETHROUGH)
  }
  if (
    !lineText.startsWith('```') &&
    countOccurrences(beforeCursor, '`') % 2 === 1
  ) {
    items.push(StyleType.CODE)
  }

  // 範囲選択時: parseInlineStyles で選択テキストのスタイルを検出
  if (from !== to) {
    const selectedText = editor.state.doc.textBetween(from, to)
    const { styles } = parseInlineStyles(selectedText)
    for (const style of styles) {
      if (!items.includes(style)) {
        items.push(style)
      }
    }
  }

  return items
}

// --- テーブル・画像挿入 ---

export const insertMarkdownTable = (
  editor: Editor,
  rows: number,
  columns: number
) => {
  const cells = Array(columns).fill('  ').join(' | ')
  const separator = Array(columns).fill('---').join(' | ')
  const lines = [
    `| ${cells} |`,
    `| ${separator} |`,
    ...Array(rows).fill(`| ${cells} |`),
  ]

  const schema = editor.state.schema
  const nodes = lines.map(line =>
    schema.node('paragraph', null, [schema.text(line)])
  )

  editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      const { $from } = state.selection
      tr.replaceWith($from.before($from.depth), $from.after($from.depth), nodes)
      return true
    })
    .run()
}

export const insertMarkdownImages = (editor: Editor, files: UploadedFile[]) => {
  const imageText = files.map(file => `![](${file.url})`).join(' ')
  editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      tr.insertText(imageText, state.selection.from, state.selection.to)
      return true
    })
    .run()
}
